import AppConstants from '../constants/app.constants';
import ErrorMessageUtils from '../utils/error-message.util';
import store from '@/common/store/index';

/**
 * フラッシュメッセージコンポーネント。
 */
export default class FlashMessage {
  private static _messageComponent: FlashMessage;
  private _addToSuccessMsgListFunc!: ((msg: string) => Promise<boolean>) | null;
  private _addToErrorMsgListFunc!: ((msg: string) => Promise<boolean>) | null;
  private _removeAllErrorMessages!: (() => void) | null;
  private _removeAllMessages!: (() => void) | null;
  private _removeDisconnectMessage!: (() => void) | null;

  /**
   * FlashMessageComponentのインスタンスを取得します。.
   *
   * @param addToSuccessMsgListFunc グローバル成功マップに新しい成功メッセージを追加するために使用される関数
   * @param addToErrorMsgListFunc グローバルエラーメッセージマップに新しいエラーメッセージを追加するために使用される関数
   * @param removeAllErrorMessages グローバルエラーマップからすべてのエラーメッセージを削除するために使用される関数
   * @param removeAllMessages グローバルからすべてのメッセージを削除するために使用される関数
   * @param removeDisconnectMessage グローバルから切断メッセージを削除するために使用される関数
   * @returns FlashMessageComponentインスタンス
   */
  public static getInstance(
    addToSuccessMsgListFunc: ((msg: string) => Promise<boolean>) | null = null,
    addToErrorMsgListFunc: ((msg: string) => Promise<boolean>) | null = null,
    removeAllErrorMessages: (() => void) | null = null,
    removeAllMessages: (() => void) | null = null,
    removeDisconnectMessage: (() => void) | null = null
  ) {
    if (!this._messageComponent) {
      this._messageComponent = new FlashMessage();
      this._messageComponent._addToSuccessMsgListFunc = addToSuccessMsgListFunc;
      this._messageComponent._addToErrorMsgListFunc = addToErrorMsgListFunc;
      this._messageComponent._removeAllErrorMessages = removeAllErrorMessages;
      this._messageComponent._removeAllMessages = removeAllMessages;
      this._messageComponent._removeDisconnectMessage = removeDisconnectMessage;
    }
    return this._messageComponent;
  }

  public static isCreated(): boolean {
    if (this._messageComponent) {
      return true;
    }
    return false;
  }

  /**
   * 成功メッセージを表示します。
   *
   * @param msg 成功メッセージ
   * @returns Promise<void>
   */
  public async showSuccessMessage(msg: string, delayMode = false): Promise<void> {
    if (!msg || !this._addToSuccessMsgListFunc) {
      return;
    }

    await this.hideAllErrorMessages();

    if (delayMode) {
      setTimeout(async () => {
        // リスト最下に新メッセージを表示
        await this._addToSuccessMsgListFunc?.(msg);
      }, 200);
      return;
    }
    // リスト最下に新メッセージを表示
    await this._addToSuccessMsgListFunc(msg);
  }

  public async hideAllErrorMessages() {
    // ストアからメッセージを削除する
    this.removeAllErrorMessages();
  }

  async hideAllMessages() {
    // ストアからメッセージを削除する
    if (this._removeAllMessages) {
      this.hideAllErrorMessages();
      await this._removeAllMessages();
    }
  }

  /**
   * 切断メッセージを非表示にする。
   */
  async hideDisconnectMessage() {
    if (this._removeDisconnectMessage) {
      await this._removeDisconnectMessage();
      this.setNetworkConnectionMode(true);
    }
  }

  private async removeAllErrorMessages() {
    if (this._removeAllErrorMessages) {
      await this._removeAllErrorMessages();
    }
  }

  /**
   * エラーメッセージ付きの閉じるアイコンを表示する
   */
  setNetworkConnectionMode(value: boolean) {
    store.commit('setNetworkConnectionMode', value);
  }

  /**
   * エラーメッセージを表示します。
   *
   * @param msg エラーメッセージ
   * @returns Promise<void>
   */
  public async showErrorMessage(msg: string, delayMode = false): Promise<void> {
    if (!this._addToErrorMsgListFunc) {
      return;
    }

    if (delayMode) {
      setTimeout(async () => {
        // リスト最下に新メッセージを表示
        await this._addToErrorMsgListFunc?.(msg);
      }, 200);
      return;
    }

    await this._addToErrorMsgListFunc(msg);
    return;
  }

  /**
   * メッセージの高さを取得
   *
   * @param   {string} msg メッセージ
   * @returns {number}     メッセージの高さ
   */
  getHeightOfMessage(msg: string) {
    // メッセージがない場合は0を返す。
    if (!msg) {
      return 0;
    }

    // 仮想要素を作成する
    const text = document.createElement('div');
    // アラート メッセージ要素のターゲットを取得する
    const elementParent = document.getElementById('alert-message-block') || document.body;
    elementParent.appendChild(text);
    text.style.fontFamily = '"Helvetica Neue", Arial, "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif';
    text.style.height = 'auto';
    // 32pxはメッセージの左アイコン
    // 30pxはメッセージの右側のアイコンです
    text.style.width = 'calc(100% - 32px - 30px)';
    text.classList.add('virtual-alert-message');
    text.innerHTML = msg;
    // 16pxはアラートメッセージのトップ/ボトムのパディング
    const msgHeight = Math.ceil(text.offsetHeight) + 16;
    // 仮想要素を削除する
    elementParent.removeChild(text);
    // デフォルトの 1 行メッセージの高さは 47pxです
    return msgHeight < AppConstants.DEFAULT_MESSAGE_HEIGHT ? AppConstants.DEFAULT_MESSAGE_HEIGHT : msgHeight;
  }

  /**
   * エラーコードとエラーメッセージをフォーマットしてエラーメッセージを作成する。
   * @param errCd はエラーコード
   * @param errMsg はエラーメッセージです
   * @returns エラーメッセージの形式
   */
  public async showErrorMsgWithErrorCode(
    errCd: string,
    errMsg: string,
    delayMode = false,
    errorMsgParamValues: Array<string> | null = null
  ) {
    if (!this._addToErrorMsgListFunc) {
      return;
    }

    if (errorMsgParamValues) {
      errMsg = ErrorMessageUtils.replacePlaceholder(errMsg, errorMsgParamValues);
    }

    if (delayMode) {
      setTimeout(async () => {
        // リスト最下に新メッセージを表示
        await this._addToErrorMsgListFunc?.(ErrorMessageUtils.buildErrorMessage(errCd, errMsg));
      }, 300);
      return;
    }

    await this._addToErrorMsgListFunc(ErrorMessageUtils.buildErrorMessage(errCd, errMsg));
  }

  /**
   * 切断エラーメッセージの表示。
   * @param {string} msg エラーメッセージ
   * @param {boolean} displayCloseIconErrMsgFlag   閉じるアイコン エラーメッセージ表示フラグ
   */
  public async showDisconnectMsg(msg: string, displayCloseIconErrMsgFlag = false) {
    this.setNetworkConnectionMode(displayCloseIconErrMsgFlag);
    await this.showErrorMessage(msg);
  }

  /**
   * 切断エラーコード メッセージ表示。
   * @param {string} errCd エラーコード
   * @param {string} errMsg エラーメッセージ
   * @param {boolean} displayCloseIconErrMsgFlag 閉じるアイコン エラーメッセージ表示フラグ
   */
  public async showDisconnectMsgWithErrorCode(errCd: string, errMsg: string, displayCloseIconErrMsgFlag = false) {
    const errorMsg = ErrorMessageUtils.buildErrorMessage(errCd, errMsg);
    await this.showDisconnectMsg(errorMsg, displayCloseIconErrMsgFlag);
  }

  /**
   * ネットワークオフラインメッセージを表示。
   */
  public async showNetworkOfflineMsg(): Promise<void> {
    const errorMsg = ErrorMessageUtils.buildNetworkOfflineMsg();
    await this.showDisconnectMsg(errorMsg);
  }
}
